// Guest-facing languages: English, Russian and Bahasa Indonesia.
//
// Catalogs are bundled JSON, not fetched: the storefront opens on a phone on a
// restaurant's Wi-Fi and a language that arrives over the network is a language
// that sometimes arrives after the guest has already read the screen. Bundled
// also means no suspense boundary, which matters because the screens inside
// /r/:slug/* all render from one mounted Shell (the URL names the step) —
// suspending mid-flow would remount it and throw the cart away.
//
// `en` is the fallback: a key that is missing from ru.json or id.json renders
// its English text rather than showing a guest a raw key.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { languageKey } from "@/lib/storageKeys";
import { EVENTS, track } from "@/lib/analytics";
import en from "@/i18n/en.json";
import ru from "@/i18n/ru.json";
import id from "@/i18n/id.json";

/** Order is the order the switcher draws them in. */
export const LANGUAGES = [
  { code: "en", short: "EN", name: "English" },
  { code: "ru", short: "RU", name: "Русский" },
  { code: "id", short: "ID", name: "Indonesia" },
];

export const SUPPORTED = LANGUAGES.map((l) => l.code);

/** BCP-47 tags for the browser APIs that want one (dates, speech recognition). */
export const LOCALE_TAG = { en: "en-US", ru: "ru-RU", id: "id-ID" };

const stored = () => {
  try {
    const saved = localStorage.getItem(languageKey);
    return SUPPORTED.includes(saved) ? saved : null;
  } catch (e) {
    return null; // private browsing with storage disabled
  }
};

/**
 * Stored choice first, then the browser's own language, then English.
 *
 * Only the prefix is used: an Indonesian phone reports `id-ID`, a Russian one
 * `ru-RU` or `ru`. `in` is Indonesian's legacy ISO code, which some browsers
 * still send.
 */
const detect = () => {
  const saved = stored();
  if (saved) return saved;
  const nav =
    (typeof navigator !== "undefined" && (navigator.language || (navigator.languages || [])[0])) ||
    "";
  const prefix = String(nav).toLowerCase().split("-")[0];
  if (prefix === "in") return "id";
  return SUPPORTED.includes(prefix) ? prefix : "en";
};

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ru: { translation: ru },
    id: { translation: id },
  },
  lng: detect(),
  fallbackLng: "en",
  supportedLngs: SUPPORTED,
  // React escapes for us; escaping here would double-encode a guest's own name.
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
  returnNull: false,
});

const applyDocumentLang = (lng) => {
  try {
    document.documentElement.lang = lng;
  } catch (e) {
    // no document (tests) — nothing to label
  }
};

applyDocumentLang(i18n.language);
i18n.on("languageChanged", applyDocumentLang);

/** Switch language and remember it for the next visit. */
export const changeLanguage = (code) => {
  if (!SUPPORTED.includes(code)) return;
  // Here rather than at the two controls that call it: one choke point means the
  // footer menu and the settings pill can never disagree about what was counted.
  if (code !== i18n.language) {
    track(EVENTS.LANGUAGE_CHANGED, { from: i18n.language, to: code });
  }
  i18n.changeLanguage(code);
  try {
    localStorage.setItem(languageKey, code);
  } catch (e) {
    // storage disabled — the choice still holds for this session
  }
};

/** The tag to hand `toLocaleDateString` and friends for the active language. */
export const localeTag = () => LOCALE_TAG[i18n.language] || LOCALE_TAG.en;

export default i18n;
